import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Award, Download, Calendar, CheckCircle, Linkedin, Eye, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { generateDiplomaPDF, generateDiplomaImage, buildPublicCertUrl, buildSocialShareUrl, sanitizeDiplomaText } from "@/lib/generateDiplomaPDF";
import { normalizeDiplomaLayout, type DiplomaLayout } from "@/hooks/useCertificateConfig";

interface CertLogRow {
  id: string;
  certificate_name: string;
  generated_at: string;
  download_count: number;
  program_id: string | null;
  program_certificate_id: string | null;
  diploma_type: 'A' | 'B' | null;
  og_image_url: string | null;
  program_certificates?: any | null;
}

function useCertificateLogs(cohortId?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['certificate-logs', user?.id, cohortId ?? 'all'],
    queryFn: async () => {
      let query = supabase
        .from('certificate_logs')
        .select('id, certificate_name, generated_at, download_count, program_id, program_certificate_id, diploma_type, og_image_url, program_certificates(*)')
        .eq('user_id', user!.id);
      // En el inicio se pasa cohortId para mostrar solo el certificado de esa cohorte;
      // en el perfil se omite para listar todos los certificados del usuario.
      if (cohortId) query = query.eq('cohort_id', cohortId);
      const { data, error } = await query.order('generated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as CertLogRow[];
    },
    enabled: !!user?.id,
  });
}

const CertificationsWidget = ({ cohortId }: { cohortId?: string }) => {
  const { profile } = useAuth();
  const { data: certLogs = [], isLoading } = useCertificateLogs(cohortId);
  const [previewCert, setPreviewCert] = useState<CertLogRow | null>(null);

  const participantName = sanitizeDiplomaText(profile?.full_name || "") || "Participante";

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });

  const getDiplomaData = (cert: CertLogRow) => {
    const pc = cert.program_certificates;
    const type = (cert.diploma_type ?? 'A') as 'A' | 'B';
    const prefix = type === 'A' ? 'diploma_a' : 'diploma_b';
    const rawEntities = pc?.[`${prefix}_entities`];
    const entities = (typeof rawEntities === 'string' && rawEntities.trim())
      ? rawEntities.trim()
      : 'Vivetori';
    return {
      name: pc?.[`${prefix}_name`] ?? cert.certificate_name,
      imageUrl: pc?.[`${prefix}_image_url`] ?? null,
      entities,
      layout: normalizeDiplomaLayout(pc?.[`${prefix}_layout`]) as DiplomaLayout,
    };
  };

  const handleDownload = async (cert: CertLogRow) => {
    const d = getDiplomaData(cert);
    try {
      await generateDiplomaPDF({
        participantName,
        diplomaName: d.name,
        certificateId: cert.id,
        issuedAt: cert.generated_at,
        backgroundImageUrl: d.imageUrl,
        publicUrl: buildPublicCertUrl(cert.id),
        entities: d.entities,
        layout: d.layout,
      });
      toast.success("Diploma descargado");
    } catch {
      toast.error("Error al descargar el diploma");
    }
  };

  const handleShareLinkedIn = (cert: CertLogRow) => {
    const d = getDiplomaData(cert);
    const issued = new Date(cert.generated_at);
    const url = new URL("https://www.linkedin.com/profile/add");
    url.searchParams.set("startTask", "CERTIFICATION_NAME");
    url.searchParams.set("name", d.name);
    url.searchParams.set("organizationName", d.entities);
    url.searchParams.set("issueYear", issued.getFullYear().toString());
    url.searchParams.set("issueMonth", (issued.getMonth() + 1).toString());
    url.searchParams.set("certId", cert.id.slice(0, 8).toUpperCase());
    url.searchParams.set("certUrl", buildPublicCertUrl(cert.id));
    window.open(url.toString(), "_blank");
    toast.success("Redirigiendo a LinkedIn");
  };

  // Publica en el muro (feed) de LinkedIn con preview automático del diploma.
  //
  // Flujo:
  //   1. Si el cert ya tiene og_image_url guardado → skip al paso 4.
  //   2. Renderiza el diploma como PNG client-side (Canvas API).
  //   3. Sube la PNG vía edge function upload-cert-og-png (populates og_image_url).
  //   4. Abre el composer de LinkedIn con la URL del edge function externo
  //      (Deno Deploy) que sirve HTML con OG tags apuntando a la PNG.
  //
  // LinkedIn scrapea la URL, ve los OG tags con la imagen del diploma
  // personalizado y muestra el preview con la imagen del participante.
  const handlePostLinkedIn = async (cert: CertLogRow) => {
    const toastId = toast.loading("Preparando publicación...");
    try {
      const d = getDiplomaData(cert);

      // Paso 1: si no hay og_image_url todavía, generarlo y subirlo.
      if (!cert.og_image_url) {
        const publicUrl = buildPublicCertUrl(cert.id);
        const pngBlob = await generateDiplomaImage({
          participantName: participantName,
          diplomaName: d.name,
          certificateId: cert.id,
          issuedAt: cert.generated_at,
          backgroundImageUrl: d.imageUrl,
          publicUrl,
          entities: d.entities,
          layout: d.layout,
        });

        const formData = new FormData();
        formData.append("file", pngBlob, `${cert.id}.png`);
        formData.append("certLogId", cert.id);

        const { error: uploadError } = await supabase.functions.invoke(
          "upload-cert-og-png",
          { body: formData },
        );
        if (uploadError) {
          // Log pero seguimos: el share va a caer al fallback template como og:image.
          console.warn("upload-cert-og-png failed:", uploadError);
        }
      }

      // Paso 2: abrir composer de LinkedIn con la URL del OG externo + texto
      // pre-cargado. Se usa `linkedin.com/feed/?shareActive=true` en vez del
      // endpoint `sharing/share-offsite/` porque el primero acepta el parámetro
      // `text` para prefill del composer (el segundo solo acepta `url`).
      const shareUrl = buildSocialShareUrl(cert.id);
      const preloadedText =
        `¡Acabo de completar el ${d.name} de LIDER IA! 🎓\n\n` +
        `Un programa de @Vivetori que me ayudó a aplicar la IA a un problema real de mi día a día.\n\n` +
        `#LIDERIA #Vivetori #InteligenciaArtificial #IA #Aprendizaje`;
      const linkedinUrl =
        `https://www.linkedin.com/feed/?shareActive=true` +
        `&text=${encodeURIComponent(preloadedText)}` +
        `&url=${encodeURIComponent(shareUrl)}` +
        `&shareUrl=${encodeURIComponent(shareUrl)}`;
      window.open(linkedinUrl, "_blank");
      toast.success("Abriendo LinkedIn con tu diploma", { id: toastId });
    } catch (e) {
      console.error("handlePostLinkedIn error", e);
      toast.error("No se pudo preparar la publicación", { id: toastId });
    }
  };

  const handleShareLink = async (cert: CertLogRow) => {
    try {
      await navigator.clipboard.writeText(buildPublicCertUrl(cert.id));
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="w-5 h-5 text-primary" />
          Mis Certificaciones
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-center text-muted-foreground py-4">Cargando...</p>
        ) : certLogs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aún no tienes certificaciones</p>
            <p className="text-sm">Completa los módulos para obtener tu primera certificación</p>
          </div>
        ) : (
          <div className="space-y-3">
            {certLogs.map((cert) => {
              const d = getDiplomaData(cert);
              return (
                <div
                  key={cert.id}
                  className="p-4 rounded-lg bg-background/50 border border-border/50 hover:border-primary/30 transition-colors space-y-3"
                >
                  {/* Fila superior: título + metadata */}
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Award className="w-5 h-5 text-primary" />
                    </div>
                    <div className="space-y-1 min-w-0 flex-1">
                      <p className="font-medium text-foreground break-words">{d.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(cert.generated_at)}
                        </span>
                        
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px] py-0">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Activo
                        </Badge>
                      </div>
                    </div>
                  </div>
                  {/* Fila inferior: acciones (siempre en su propia línea, alineadas a la izquierda) */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => handleShareLink(cert)} title="Copiar enlace público">
                      <Share2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Enlace</span>
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => handleShareLinkedIn(cert)} title="Añadir a mi perfil de LinkedIn">
                      <Linkedin className="w-4 h-4" />
                      <span className="hidden sm:inline">Añadir a LinkedIn</span>
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => handlePostLinkedIn(cert)} title="Publicar en mi muro de LinkedIn">
                      <Linkedin className="w-4 h-4" />
                      <span className="hidden sm:inline">Publicar en LinkedIn</span>
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => setPreviewCert(cert)} title="Vista previa del diploma">
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">Ver</span>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={!!previewCert} onOpenChange={() => setPreviewCert(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              Vista Previa del Diploma
            </DialogTitle>
          </DialogHeader>

          {previewCert && (() => {
            const d = getDiplomaData(previewCert);
            const L = d.layout;
            const date = formatDate(previewCert.generated_at);
            const shortId = previewCert.id.slice(0, 8).toUpperCase();
            const publicUrl = buildPublicCertUrl(previewCert.id);

            const renderEl = (
              el: typeof L.participantName,
              text: string,
              key: string,
              bold = false,
            ) => {
              if (!el.enabled || !text) return null;
              const transform =
                el.align === 'center' ? 'translate(-50%, -50%)'
                : el.align === 'right' ? 'translate(-100%, -50%)'
                : 'translate(0, -50%)';
              return (
                <div
                  key={key}
                  className="absolute whitespace-nowrap"
                  style={{
                    left: `${el.x}%`,
                    top: `${el.y}%`,
                    transform,
                    fontSize: `${Math.max(8, el.fontSize * 0.55)}px`,
                    color: el.color,
                    fontWeight: bold ? 700 : 400,
                    textShadow: '0 1px 2px rgba(255,255,255,0.5)',
                  }}
                >
                  {text}
                </div>
              );
            };

            return (
              <>
                <div className="p-6 pt-4">
                  <div className="relative w-full aspect-[297/210] rounded-lg overflow-hidden bg-muted">
                    {d.imageUrl ? (
                      <img src={d.imageUrl} alt="Diploma" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 bg-slate-900" />
                    )}
                    {renderEl(L.participantName, participantName, 'name', true)}
                    {renderEl(L.diplomaName, d.name, 'dname')}
                    {renderEl(L.date, `Fecha de emisión: ${date}`, 'date')}
                    {renderEl(L.certificateId, `ID: ${shortId}`, 'cid')}
                    {renderEl(L.entities, `Emitido por: ${d.entities}`, 'ent')}
                    {renderEl(L.publicUrl, `Verificar: ${publicUrl}`, 'url')}
                    {L.qr.enabled && (
                      <div
                        className="absolute bg-white border border-border flex items-center justify-center text-[10px] text-muted-foreground font-mono"
                        style={{
                          left: `${L.qr.x}%`,
                          top: `${L.qr.y}%`,
                          width: `${(L.qr.size / 297) * 100}%`,
                          aspectRatio: '1 / 1',
                          transform: 'translate(-50%, -50%)',
                        }}
                      >
                        QR
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-3 text-center">
                    URL pública: <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{publicUrl}</a>
                  </p>
                </div>
                <DialogFooter className="p-6 pt-0 gap-2">
                  <Button variant="outline" onClick={() => setPreviewCert(null)}>Cerrar</Button>
                  <Button className="gap-2" onClick={() => { handleDownload(previewCert); setPreviewCert(null); }}>
                    <Download className="w-4 h-4" /> Descargar PDF
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CertificationsWidget;
